import type { SupabaseClient } from '@supabase/supabase-js';
import { env, supabaseConfigured } from '../config/env';

/**
 * Lazy Supabase client.
 *
 * The library is code-split and imported on first use, which keeps ~110kB of
 * gzipped JS off the first paint. Nothing on the guest's critical path needs it:
 * the web upload happens after the clip is already on screen, and the kiosk
 * queue runs in the background. The kiosk board is our performance floor and
 * mobile web arrives from an ad, so neither can afford to parse it up front.
 *
 * Returns null when the project is not configured, so a dev machine with no
 * .env still runs the whole flow — uploads simply no-op.
 */

let clientPromise: Promise<SupabaseClient> | null = null;

export function getSupabase(): Promise<SupabaseClient | null> {
  if (!supabaseConfigured) return Promise.resolve(null);
  if (!clientPromise) {
    clientPromise = import('@supabase/supabase-js')
      .then(({ createClient }) =>
        createClient(env.supabaseUrl, env.supabaseAnonKey, {
          auth: { persistSession: false, autoRefreshToken: false },
        }),
      )
      .catch((err: unknown) => {
        clientPromise = null;
        throw err instanceof Error ? err : new Error(String(err));
      });
  }
  return clientPromise;
}

export const clipBucket = env.supabaseBucket;

/**
 * Uploads a clip to the private `clips` bucket.
 *
 * `upsert: false` is deliberate. Supabase's `upsert: true` sends `x-upsert`,
 * which makes storage-api do an INSERT ... ON CONFLICT DO UPDATE on
 * `storage.objects` — and 0001_init.sql grants anon INSERT there and nothing
 * else, so every upload fails with an RLS refusal that reads identically to a
 * `sessions` failure. MHA used `x-upsert` and granted anon UPDATE on the bucket
 * to match; we do not, because a plain insert plus duplicate-as-success gets the
 * same retry behaviour without letting anon overwrite objects it did not write.
 *
 * A duplicate means a previous attempt already stored this exact object. Storage
 * uploads are atomic per request — there is no half-written object to repair —
 * so treating it as success is correct, and it is what makes the kiosk queue's
 * retries idempotent.
 */
export async function uploadClip(
  supabase: SupabaseClient,
  path: string,
  blob: Blob,
  contentType: string,
): Promise<{ error: { message: string } | null }> {
  const { error } = await supabase.storage
    .from(clipBucket)
    .upload(path, blob, { contentType, upsert: false });

  if (!error) return { error: null };
  if (isAlreadyExists(error)) return { error: null };
  return { error };
}

function isAlreadyExists(error: { message?: string; statusCode?: string }): boolean {
  const status = (error as { statusCode?: string }).statusCode;
  return status === '409' || /already exists|duplicate/i.test(error.message ?? '');
}

/**
 * Writes a session row through the `record_session` RPC. Never write the table
 * directly from the client — neither an update nor an upsert can work.
 *
 * `sessions` grants anon no SELECT policy, deliberately: it holds guests' phone
 * numbers and all reporting runs with the service role. Two consequences follow,
 * and both are silent:
 *
 *   - PostgREST's upsert issues INSERT ... ON CONFLICT DO UPDATE, whose update
 *     path must read the conflicting row. Refused outright with 42501.
 *   - A plain UPDATE ... WHERE session_id = x is worse. Postgres applies SELECT
 *     policies to the rows an UPDATE reads for its WHERE clause, so it matches
 *     nothing and returns HTTP 204 with a zero-row content-range — success,
 *     zero rows written, no error raised anywhere.
 *
 * The "anon can update own session" policy in 0001_init.sql is unreachable for
 * that reason. A SECURITY DEFINER function is the sanctioned path instead, which
 * is what MHA used on this stack (`set_session_phone`); the guards RLS would
 * have enforced live in the function body. See 0003_record_session_rpc.sql.
 *
 * Both writers (session logging and the kiosk delivery queue) send only their
 * own columns. Omitted arguments arrive as null, which the function reads as
 * "leave alone", so neither clobbers the other and arrival order does not
 * matter — the property the upsert was there for.
 */
export async function writeSessionRow<T extends { session_id: string }>(
  supabase: SupabaseClient,
  row: T,
): Promise<{ error: { message: string } | null }> {
  // Column names map 1:1 onto the function's p_-prefixed arguments.
  const args: Record<string, unknown> = {};
  for (const [column, value] of Object.entries(row)) {
    if (value !== undefined) args[`p_${column}`] = value;
  }

  const { error } = await supabase.rpc('record_session', args);
  return { error: error ? describe('record_session', error) : null };
}

/**
 * Which of the two statements failed, and the Postgres code. Without this an
 * RLS refusal reads the same from either step, and they have different causes.
 */
function describe(
  step: string,
  error: { code?: string; message?: string; details?: string | null },
): { message: string } {
  const code = error.code ? ` [${error.code}]` : '';
  const details = error.details ? ` — ${error.details}` : '';
  return { message: `sessions ${step}${code}: ${error.message ?? 'unknown'}${details}` };
}

/** Storage path for a clip. Date prefix keeps the bucket browsable by event. */
export function storagePathFor(sessionId: string, extension: string, mode: string): string {
  const day = new Date().toISOString().slice(0, 10);
  return `${mode}/${day}/${sessionId}.${extension}`;
}
